import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Calendar, MapPin, Users, CreditCard, 
  Phone, Mail, FileText, Clock, CheckCircle, XCircle, AlertCircle,
  Plane, Hotel, Car, ArrowLeft, User, Briefcase, ListOrdered, Moon,
  ScrollText, RotateCcw, Edit3, MessageCircle, Download, Send, Loader2, FileType
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { generateQuotePdfDoc } from "../../utils/quotePdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatCurrency = (amount) => {
  if (!amount) return '₹0';
  return `₹${amount.toLocaleString('en-IN')}`;
};

const calculateNights = (travelDate, returnDate) => {
  if (!travelDate || !returnDate) return 0;
  const start = new Date(travelDate);
  const end = new Date(returnDate);
  return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
};

const parseDayDate = (dayDateStr, travelDate, dayIndex) => {
  if (dayDateStr) {
    const isoDate = new Date(dayDateStr + 'T00:00:00');
    if (!isNaN(isoDate.getTime())) return isoDate;
    const parsed = new Date(dayDateStr);
    if (!isNaN(parsed.getTime())) return parsed;
  }
  if (travelDate) {
    const base = new Date(travelDate + 'T00:00:00');
    if (!isNaN(base.getTime())) {
      const d = new Date(base);
      d.setDate(d.getDate() + dayIndex);
      return d;
    }
  }
  return null;
};

const formatDayDateLabel = (date) => {
  if (!date) return '';
  const dayAbbr = date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
  const dayOfMonth = String(date.getDate()).padStart(2, '0');
  const monthNum = String(date.getMonth() + 1).padStart(2, '0');
  return `${dayAbbr} - ${dayOfMonth}/${monthNum}/${date.getFullYear()}`;
};

const AdminQuotePreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  const [emailDialog, setEmailDialog] = useState(false);
  const [emailForm, setEmailForm] = useState({ to_email: "", subject: "", message: "", include_pdf: true, include_docx: false, cc_self: true });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [confirmingBooking, setConfirmingBooking] = useState(false);
  const [guestDialogOpen, setGuestDialogOpen] = useState(false);
  const [guestForm, setGuestForm] = useState({ guest_name: "", guest_mobile: "", guest_email: "" });

  // For Direct Client quotes only: admin confirms the booking directly
  // (since there is no agent to do it). Opens an editable Guest Info dialog
  // so the admin can fill / correct guest details before confirming.
  const openGuestDialog = () => {
    if (!quote) return;
    setGuestForm({
      guest_name: (quote.client_name || quote.guest_name || "").trim(),
      guest_mobile: (quote.client_phone || quote.guest_mobile || "").trim(),
      guest_email: (quote.client_email || quote.guest_email || "").trim(),
    });
    setGuestDialogOpen(true);
  };

  const confirmDirectBooking = async () => {
    if (!quote) return;
    const guestName = (guestForm.guest_name || "").trim();
    const guestPhone = (guestForm.guest_mobile || "").trim();
    const guestEmail = (guestForm.guest_email || "").trim();
    if (!guestName) {
      toast.error("Guest Name is required before confirming the booking.");
      return;
    }
    if (!guestPhone) {
      toast.error("Guest Mobile is required before confirming the booking.");
      return;
    }
    try {
      setConfirmingBooking(true);
      const res = await axios.post(`${API}/bookings`, {
        quote_id: quote.id,
        client_name: guestName,
        client_phone: guestPhone,
        client_email: guestEmail,
      }, { headers: { Authorization: `Bearer ${token}` } });
      const newBookingId = res.data?.booking_id || res.data?.id;
      toast.success("Booking confirmed!");
      setGuestDialogOpen(false);
      if (newBookingId) {
        navigate(`/admin/bookings/${newBookingId}`);
      } else {
        navigate('/admin/bookings');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to confirm booking");
    } finally {
      setConfirmingBooking(false);
    }
  };

  const triggerDownload = (base64, filename, mime) => {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  const downloadPdf = async (withPrice = true) => {
    if (!quote) return;
    setDownloadingPdf(true);
    try {
      const agent = {
        company_name: user?.company_name || 'Happy Holidays DMC',
        name: user?.name || '',
        phone: user?.phone || '',
        email: user?.email || '',
        address: user?.address || '',
        city: user?.city || '',
        state: user?.state || '',
        website: user?.website || '',
        logo_url: user?.logo_url || '',
        profile_picture_url: user?.profile_picture_url || '',
        designation: user?.designation || '',
      };
      const doc = generateQuotePdfDoc(quote, agent, {
        agentMarkup: { adult: 0, cwb: 0, cnb: 0 },
        markupMode: 'manual',
        markupPercentage: 0,
        withPrice,
      });
      if (!doc) {
        toast.error('Quote data missing — cannot generate PDF');
        return;
      }
      const suffix = withPrice ? '_with_price' : '_without_price';
      doc.save(`Quotation_${quote.quote_number || quote.id.slice(0, 8)}${suffix}.pdf`);
      toast.success(withPrice ? 'PDF downloaded (with price)' : 'PDF downloaded (without price)');
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const downloadDocx = async () => {
    if (!quote) return;
    setDownloadingDocx(true);
    try {
      const res = await axios.get(`${API}/quotes/${quote.id}/docx`, { headers: { Authorization: `Bearer ${token}` } });
      triggerDownload(res.data.docx, res.data.filename || `quote_${quote.id.slice(0,8)}.docx`, 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');
      toast.success('Word document downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate Word document');
    } finally {
      setDownloadingDocx(false);
    }
  };

  const openEmailDialog = () => {
    if (!quote) return;
    setEmailForm({
      to_email: quote.client_email || "",
      subject: `Travel Quotation ${quote.quote_number || '#' + quote.id.slice(0,8).toUpperCase()} — ${quote.destination || 'Trip'}`,
      message: `Dear ${quote.client_name || 'Traveller'},\n\nPlease find your travel quotation attached. Let us know if you have any questions or would like adjustments.\n\nWarm regards,`,
      include_pdf: true,
      include_docx: false,
      cc_self: true,
    });
    setEmailDialog(true);
  };

  const sendEmail = async () => {
    if (!emailForm.to_email || !/.+@.+\..+/.test(emailForm.to_email)) {
      toast.error('Please enter a valid recipient email');
      return;
    }
    setSendingEmail(true);
    try {
      const res = await axios.post(`${API}/quotes/${quote.id}/share-email`, emailForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Email sent to ${res.data.to}`);
      setEmailDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/quotes/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setQuote(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load quote");
      } finally {
        setLoading(false);
      }
    };
    fetchQuote();
  }, [id, token]);

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: "bg-amber-100 text-amber-700", icon: Clock },
      quoted: { bg: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
      approved: { bg: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
      rejected: { bg: "bg-red-100 text-red-700", icon: XCircle },
      booked: { bg: "bg-blue-100 text-blue-700", icon: CheckCircle },
      revision_requested: { bg: "bg-orange-100 text-orange-700", icon: RotateCcw },
      confirmed: { bg: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
      lost: { bg: "bg-red-100 text-red-700", icon: XCircle },
    };
    const config = styles[status] || { bg: "bg-gray-100 text-gray-700", icon: AlertCircle };
    const Icon = config.icon;
    return (
      <Badge className={`${config.bg} flex items-center gap-1 px-3 py-1`}>
        <Icon className="w-3.5 h-3.5" />
        <span className="capitalize">{status}</span>
      </Badge>
    );
  };

  const getHotelStarLabel = (category) => {
    if (!category) return '-';
    const lower = category.toLowerCase();
    if (lower.includes('5')) return '5 Star';
    if (lower.includes('4')) return '4 Star';
    if (lower.includes('3')) return '3 Star';
    if (lower.includes('budget')) return 'Budget';
    return category;
  };

  if (loading) {
    return (
      <DashboardLayout type="admin">
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !quote) {
    return (
      <DashboardLayout type="admin">
        <div className="flex flex-col items-center justify-center py-20">
          <XCircle className="w-16 h-16 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Quote</h2>
          <p className="text-gray-500">{error || "Quote not found"}</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/admin/quotes')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Quotes
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const nights = quote.nights || calculateNights(quote.travel_date, quote.return_date);
  
  // Accommodation options
  const accommodationOptions = Array.isArray(quote.accommodation_options) && quote.accommodation_options.length > 0 
    ? quote.accommodation_options : [];
  const hasMultipleOptions = accommodationOptions.length > 1;
  const getActiveOption = () => hasMultipleOptions ? (accommodationOptions[0] || {}) : quote;
  const activeOption = getActiveOption();
  
  const hotels = Array.isArray(activeOption.hotels) && activeOption.hotels.length > 0 
    ? activeOption.hotels 
    : (Array.isArray(quote.hotels) ? quote.hotels : []);
  const activities = Array.isArray(quote.activities) ? quote.activities : [];
  const flights = Array.isArray(quote.flights) ? quote.flights.filter(f => f.airline || f.flight_number) : [];

  // Build itinerary
  const buildItineraryFromData = () => {
    if (!quote.travel_date || !quote.return_date) return [];
    const start = new Date(quote.travel_date);
    const end = new Date(quote.return_date);
    const daysList = [];
    let dayNum = 1;
    const transfersDetails = quote.transfers_details || [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      const hotelForDay = hotels.find(h => {
        if (!h.check_in || !h.check_out) return false;
        const checkIn = new Date(h.check_in); checkIn.setHours(0,0,0,0);
        const checkOut = new Date(h.check_out); checkOut.setHours(0,0,0,0);
        const current = new Date(dateStr); current.setHours(0,0,0,0);
        return current >= checkIn && current < checkOut;
      });
      const activitiesForDay = activities.filter(a => a.day === dayNum);
      const transferForDay = transfersDetails.find(t => t.day === dayNum);
      const sightseeingNamesForDay = activitiesForDay
        .filter(a => a.type !== 'entry_ticket')
        .map(a => a.tour_name || a.name || a.title)
        .filter(Boolean);
      daysList.push({
        day: dayNum, date: dateStr,
        title: hotelForDay?.city || quote.destination || `Day ${dayNum}`,
        city: hotelForDay?.city || '',
        hotel: hotelForDay ? `${hotelForDay.hotel_name} (${hotelForDay.hotel_category})` : null,
        activities: activitiesForDay.map(a => ({ name: a.tour_name || a.name || a.title, type: a.type, description: a.description })),
        sightseeing: sightseeingNamesForDay.join(', '),
        transfer_type: transferForDay?.transfer_type || null,
        vehicle: transferForDay?.type || null,
        description: sightseeingNamesForDay.length > 0 
          ? `Sightseeing: ${sightseeingNamesForDay.join(', ')}`
          : (hotelForDay ? `Stay at ${hotelForDay.hotel_name}` : '')
      });
      dayNum++;
    }
    return daysList;
  };

  const itinerary = (Array.isArray(quote.itinerary) && quote.itinerary.length > 0)
    ? quote.itinerary : buildItineraryFromData();

  return (
    <DashboardLayout type="admin">
      <div className="space-y-6 max-w-6xl mx-auto" data-testid="admin-quote-preview">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="font-mono text-emerald-600 font-bold bg-emerald-50 px-3 py-1.5 rounded-lg text-lg">
                #{quote.quote_number || quote.id.slice(0, 8).toUpperCase()}
              </span>
              {getStatusBadge(quote.status)}
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Sent Quotation Preview</h1>
            <p className="text-sm text-gray-500 mt-0.5">This is exactly how the agent sees the quotation</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => navigate(`/admin/quote-builder/${id}`)}
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
              data-testid="preview-edit-quote-btn"
            >
              <Edit3 className="w-4 h-4" /> Edit Quote
            </Button>
            {/* Direct Client quotes: admin can confirm booking right here */}
            {quote.agent_id === "DIRECT" && (quote.status === "quoted" || quote.status === "approved") && (
              <Button
                onClick={openGuestDialog}
                disabled={confirmingBooking}
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                data-testid="preview-confirm-direct-booking-btn"
              >
                <CheckCircle className="w-4 h-4" />
                {confirmingBooking ? "Confirming..." : "Confirm Booking"}
              </Button>
            )}
            {quote.agent_id && quote.agent_id !== "DIRECT" && (
              <Button
                variant="outline"
                onClick={() => navigate(`/admin/chat?to=${quote.agent_id}&quote_id=${quote.id}&quote_number=${quote.quote_number || ''}`)}
                className="border-emerald-300 text-emerald-600 hover:bg-emerald-50 gap-2"
                data-testid="preview-chat-btn"
              >
                <MessageCircle className="w-4 h-4" /> Chat with Agent
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate('/admin/quotes')} data-testid="preview-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Quotes
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Trip Overview */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Plane className="w-5 h-5 text-emerald-600" />
                  Trip Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Quotation No</p>
                      <p className="font-semibold text-emerald-600">#{quote.quote_number || quote.id.slice(0, 8).toUpperCase()}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Destination</p>
                      <p className="font-semibold text-gray-800">{quote.destination || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Travelers</p>
                      <p className="font-semibold text-gray-800">
                        {quote.pax_adults || 0} Adults{(quote.cwb || 0) > 0 ? `, ${quote.cwb} CWB` : ''}{(quote.cnb || 0) > 0 ? `, ${quote.cnb} CNB` : ''}{(quote.pax_infants || 0) > 0 ? `, ${quote.pax_infants} Infants` : ''}
                      </p>
                      {quote.cwb_ages && <p className="text-xs text-gray-400 mt-0.5">CWB Ages: {quote.cwb_ages} yrs</p>}
                      {quote.cnb_ages && <p className="text-xs text-gray-400 mt-0.5">CNB Ages: {quote.cnb_ages} yrs</p>}
                      {quote.infant_ages && <p className="text-xs text-gray-400 mt-0.5">Infant Ages: {quote.infant_ages} yrs</p>}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Moon className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">No of Nights</p>
                      <p className="font-semibold text-gray-800">{nights} Nights</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Travel Date</p>
                      <p className="font-semibold text-gray-800">{formatDate(quote.travel_date)}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Return Date</p>
                      <p className="font-semibold text-gray-800">{formatDate(quote.return_date)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Guest Information */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <User className="w-5 h-5 text-blue-600" />
                  Guest Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Guest Name</p>
                      <p className="font-semibold text-gray-800">{quote.client_name || <span className="text-gray-400 italic">Not filled</span>}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Guest Mobile</p>
                      <p className="font-semibold text-gray-800">{quote.client_phone || <span className="text-gray-400 italic">Not filled</span>}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Flight Details */}
            {flights.length > 0 && (
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Plane className="w-5 h-5 text-sky-600" />
                    Flight Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {flights.map((fl, idx) => (
                      <div key={idx} className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                        {flights.length > 1 && (
                          <p className="text-[10px] font-bold text-sky-600 uppercase tracking-wider mb-2">Flight {idx + 1}</p>
                        )}
                        <div className="flex flex-col md:flex-row items-stretch gap-4">
                          <div className="flex flex-col items-center justify-center gap-2 min-w-[140px] pr-4 md:border-r border-gray-200">
                            <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center">
                              <Plane className="w-5 h-5 text-sky-600" />
                            </div>
                            <p className="font-semibold text-gray-900 text-sm">{fl.airline || '-'}</p>
                            <p className="text-xs text-gray-500">{fl.flight_number || '-'}</p>
                            {fl.pnr && (
                              <div className="flex items-center gap-1 mt-1">
                                <span className="text-[10px] font-bold text-white bg-emerald-500 px-2 py-0.5 rounded">PNR</span>
                                <span className="text-xs font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">{fl.pnr}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 text-center">
                            <p className="text-xl font-bold text-gray-900">{fl.departure_city || '-'}</p>
                            <p className="text-sm text-gray-800 mt-1">
                              <span className="font-semibold">{fl.departure_code}</span>
                              {fl.departure_time && <span className="ml-2 text-lg font-bold">{fl.departure_time}</span>}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">{fl.departure_date}</p>
                          </div>
                          <div className="flex flex-col items-center justify-center gap-1 px-3">
                            <div className="flex items-center gap-2 w-full">
                              <div className="w-2 h-2 rounded-full border-2 border-sky-500" />
                              <div className="flex-1 border-t-2 border-dashed border-gray-300 relative">
                                <Plane className="w-4 h-4 text-sky-500 absolute -top-2 left-1/2 -translate-x-1/2" />
                              </div>
                              <div className="w-2 h-2 rounded-full bg-sky-500" />
                            </div>
                            {fl.duration && <p className="text-xs text-gray-500 font-medium">{fl.duration}{(fl.connection_type || 'non_stop') === 'non_stop' ? ' : Non Stop' : (fl.stops ? ` : ${fl.stops} stop` : '')}</p>}
                          </div>
                          <div className="flex-1 text-center">
                            <p className="text-xl font-bold text-gray-900">{fl.arrival_city || '-'}</p>
                            <p className="text-sm text-gray-800 mt-1">
                              {fl.arrival_time && <span className="text-lg font-bold mr-2">{fl.arrival_time}</span>}
                              <span className="font-semibold">{fl.arrival_code}</span>
                            </p>
                            <p className="text-xs text-gray-400 mt-1">{fl.arrival_date}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Hotel Details */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Hotel className="w-5 h-5 text-purple-600" />
                  Hotel Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                {hasMultipleOptions ? (
                  <div className="space-y-10">
                    {accommodationOptions.map((opt, optIdx) => {
                      const optHotels = opt.hotels || [];
                      const optBadgeColor = optIdx === 0 ? 'bg-blue-100 text-blue-700' : optIdx === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700';
                      return (
                        <div key={`admin-opt-group-${optIdx}`} data-testid={`admin-hotel-option-group-${optIdx}`} className={optIdx > 0 ? "pt-6 border-t border-gray-200" : ""}>
                          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
                            <Badge className={`${optBadgeColor} text-xs font-bold tracking-wider`}>OPTION {optIdx + 1}</Badge>
                          </div>
                          {optHotels.length > 0 ? (
                            <div className="space-y-4">
                              {optHotels.map((hotel, idx) => (
                                <div key={`${optIdx}-${idx}`} className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                                  <div className="flex justify-between items-start mb-3">
                                    <div>
                                      <p className="font-semibold text-gray-800 text-lg">{hotel.hotel_name || hotel.name}</p>
                                      <p className="text-sm text-gray-500">{hotel.city || hotel.destination}</p>
                                    </div>
                                    <Badge className="bg-purple-100 text-purple-700">{getHotelStarLabel(hotel.category || hotel.hotel_category || quote.hotel_category)}</Badge>
                                  </div>
                                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
                                    <div><p className="text-gray-500">No of Rooms</p><p className="font-medium text-gray-800">{hotel.rooms_count || hotel.rooms || quote.no_of_rooms || 1}</p></div>
                                    <div><p className="text-gray-500">Room Category</p><p className="font-medium text-gray-800">{hotel.room_type || quote.room_type || '-'}</p></div>
                                    <div><p className="text-gray-500">Meal Plan</p><p className="font-medium text-gray-800">{(hotel.meal_plan || quote.meal_plan || '-').toUpperCase()}</p></div>
                                    <div><p className="text-gray-500">Nights</p><p className="font-medium text-gray-800">{hotel.nights || '-'}</p></div>
                                    <div><p className="text-gray-500">Check In</p><p className="font-medium text-gray-800">{formatDate(hotel.check_in)}</p></div>
                                    <div><p className="text-gray-500">Check Out</p><p className="font-medium text-gray-800">{formatDate(hotel.check_out)}</p></div>
                                    {hotel.extra_bed_toggle && hotel.extra_bed_count > 0 && <div><p className="text-gray-500">EB / EM</p><p className="font-medium text-gray-800">{hotel.extra_bed_count}</p></div>}
                                    {hotel.cwb_toggle && hotel.cwb_count > 0 && <div><p className="text-gray-500">CWB</p><p className="font-medium text-gray-800">{hotel.cwb_count}</p></div>}
                                    {hotel.cnb_toggle && hotel.cnb_count > 0 && <div><p className="text-gray-500">CNB</p><p className="font-medium text-gray-800">{hotel.cnb_count}</p></div>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-400 italic pl-1">No hotels configured in this option</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : hotels && hotels.length > 0 ? (
                  <div className="space-y-4">
                    {hotels.map((hotel, idx) => (
                      <div key={idx} className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-semibold text-gray-800 text-lg">{hotel.hotel_name || hotel.name}</p>
                            <p className="text-sm text-gray-500">{hotel.city || hotel.destination}</p>
                          </div>
                          <Badge className="bg-purple-100 text-purple-700">
                            {getHotelStarLabel(hotel.category || hotel.hotel_category || quote.hotel_category)}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
                          <div><p className="text-gray-500">No of Rooms</p><p className="font-medium text-gray-800">{hotel.rooms_count || hotel.rooms || quote.no_of_rooms || 1}</p></div>
                          <div><p className="text-gray-500">Room Category</p><p className="font-medium text-gray-800">{hotel.room_type || quote.room_type || '-'}</p></div>
                          <div><p className="text-gray-500">Meal Plan</p><p className="font-medium text-gray-800">{(hotel.meal_plan || quote.meal_plan || '-').toUpperCase()}</p></div>
                          <div><p className="text-gray-500">Nights</p><p className="font-medium text-gray-800">{hotel.nights || '-'}</p></div>
                          <div><p className="text-gray-500">Check In</p><p className="font-medium text-gray-800">{formatDate(hotel.check_in)}</p></div>
                          <div><p className="text-gray-500">Check Out</p><p className="font-medium text-gray-800">{formatDate(hotel.check_out)}</p></div>
                          {hotel.extra_bed_toggle && hotel.extra_bed_count > 0 && <div><p className="text-gray-500">EB / EM</p><p className="font-medium text-gray-800">{hotel.extra_bed_count}</p></div>}
                          {hotel.cwb_toggle && hotel.cwb_count > 0 && <div><p className="text-gray-500">CWB</p><p className="font-medium text-gray-800">{hotel.cwb_count}</p></div>}
                          {hotel.cnb_toggle && hotel.cnb_count > 0 && <div><p className="text-gray-500">CNB</p><p className="font-medium text-gray-800">{hotel.cnb_count}</p></div>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : quote.hotel_name ? (
                  <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-semibold text-gray-800 text-lg">{quote.hotel_name}</p>
                        <p className="text-sm text-gray-500">{quote.destination}</p>
                      </div>
                      <Badge className="bg-purple-100 text-purple-700">{getHotelStarLabel(quote.hotel_category)}</Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                      <div><p className="text-gray-500">No of Rooms</p><p className="font-medium text-gray-800">{quote.no_of_rooms || 1}</p></div>
                      <div><p className="text-gray-500">Room Category</p><p className="font-medium text-gray-800">{quote.room_type || '-'}</p></div>
                      <div><p className="text-gray-500">Check In</p><p className="font-medium text-gray-800">{formatDate(quote.hotel_check_in || quote.travel_date)}</p></div>
                      <div><p className="text-gray-500">Check Out</p><p className="font-medium text-gray-800">{formatDate(quote.hotel_check_out || quote.return_date)}</p></div>
                      <div><p className="text-gray-500">Meal Plan</p><p className="font-medium text-gray-800">{quote.meal_plan || '-'}</p></div>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No hotel details available</p>
                )}
              </CardContent>
            </Card>

            {/* Day-wise Itinerary */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ListOrdered className="w-5 h-5 text-indigo-600" />
                  Day-wise Itinerary
                </CardTitle>
              </CardHeader>
              <CardContent>
                {itinerary && itinerary.length > 0 ? (
                  <div className="space-y-0">
                    {itinerary.map((day, idx) => {
                      const dayDate = parseDayDate(day.date, quote.travel_date, idx);
                      const dateLabel = formatDayDateLabel(dayDate);
                      const vehicleName = day.vehicle || '';
                      const rawTransfer = (day.transfer_type || '').toLowerCase();
                      const isNoTransfer = rawTransfer === 'none' || rawTransfer === 'no_transfer' || rawTransfer === 'no-transfer';
                      const transferLabel = isNoTransfer ? null
                        : rawTransfer === 'sic' || rawTransfer === 'seat_in_coach' || rawTransfer === 'seat-in-coach'
                        ? 'Seat in Coach (SIC)'
                        : 'Private'; // legacy/unset default — always show transfer mode unless explicitly "no transfer"
                      return (
                        <div key={idx} className="relative">
                          <div className="bg-gray-50/80 rounded-xl p-5 border border-gray-100 mb-3">
                            <div className="mb-3">
                              <h4 className="text-base font-bold text-gray-900 tracking-wide uppercase">
                                DAY {day.day || idx + 1}: {dateLabel}{(day.city || day.title) && idx !== itinerary.length - 1 ? ` - ${(day.city || day.title).toUpperCase()}` : ''}
                              </h4>
                            </div>
                            {day.activities && Array.isArray(day.activities) && day.activities.length > 0 && (
                              <div className="space-y-3 mb-4">
                                {day.activities.map((activity, aIdx) => {
                                  const actName = typeof activity === 'string' ? activity : (activity.title || activity.name);
                                  const actDesc = typeof activity === 'object' ? activity.description : null;
                                  return (
                                    <div key={aIdx}>
                                      <p className="text-sm font-semibold text-gray-800">{actName}</p>
                                      {actDesc && <p className="text-sm text-gray-500 mt-0.5">{actDesc}</p>}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            {day.description && (!day.activities || day.activities.length === 0) && (
                              <p className="text-sm text-gray-600 leading-relaxed mb-4 whitespace-pre-line">{day.description}</p>
                            )}
                            <div className="flex flex-wrap gap-4 pt-3 border-t border-gray-200">
                              {day.hotel && !hasMultipleOptions && (
                                <div className="flex items-center gap-2 text-sm">
                                  <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center">
                                    <Hotel className="w-4 h-4 text-indigo-600" />
                                  </div>
                                  <div>
                                    <span className="text-xs text-gray-400 block leading-none">Accommodation</span>
                                    <span className="text-gray-800 font-semibold text-sm">{day.hotel}</span>
                                  </div>
                                </div>
                              )}
                              {transferLabel && (
                                <div className="flex items-center gap-2 text-sm">
                                  <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center">
                                    <Car className="w-4 h-4 text-amber-600" />
                                  </div>
                                  <div>
                                    <span className="text-xs text-gray-400 block leading-none">Transfer</span>
                                    <span className="text-gray-800 font-semibold text-sm">{transferLabel}</span>
                                  </div>
                                </div>
                              )}
                              {vehicleName && (
                                <div className="flex items-center gap-2 text-sm">
                                  <div className="w-7 h-7 rounded-lg bg-sky-100 flex items-center justify-center">
                                    <Car className="w-4 h-4 text-sky-600" />
                                  </div>
                                  <div>
                                    <span className="text-xs text-gray-400 block leading-none">Vehicle</span>
                                    <span className="text-gray-800 font-semibold text-sm">{vehicleName}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : typeof quote.itinerary === 'string' && quote.itinerary ? (
                  <div className="text-sm text-gray-600 whitespace-pre-line bg-gray-50 p-4 rounded-lg">{quote.itinerary}</div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <ListOrdered className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No itinerary details available</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Inclusions & Exclusions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-white border-gray-200 shadow-sm border-l-4 border-l-emerald-500">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg text-emerald-600">
                    <CheckCircle className="w-5 h-5" /> Inclusions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {quote.inclusions ? (
                    <ul className="space-y-2">
                      {quote.inclusions.split('\n').filter(line => line.trim()).map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{item.replace(/^[-•*]\s*/, '')}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-400 text-sm text-center py-4">No inclusions specified</p>
                  )}
                </CardContent>
              </Card>
              <Card className="bg-white border-gray-200 shadow-sm border-l-4 border-l-red-500">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg text-red-600">
                    <XCircle className="w-5 h-5" /> Exclusions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {quote.exclusions ? (
                    <ul className="space-y-2">
                      {quote.exclusions.split('\n').filter(line => line.trim()).map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{item.replace(/^[-•*]\s*/, '')}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-400 text-sm text-center py-4">No exclusions specified</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Terms & Conditions */}
            {quote.show_terms_conditions && quote.terms_conditions && (
              <Card className="bg-white border-gray-200 shadow-sm border-l-4 border-l-indigo-500">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg text-indigo-600">
                    <ScrollText className="w-5 h-5" /> Terms & Conditions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {quote.terms_conditions.split('\n').filter(line => line.trim()).map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-indigo-400 mt-0.5 flex-shrink-0">&#8226;</span>
                        <span className="text-gray-700">{item.replace(/^[-•*]\s*/, '')}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Multi-Option DMC Price Cards — one per option (matches agent view) */}
            {hasMultipleOptions && accommodationOptions.map((opt, idx) => {
              const dmcPerAdult = opt.quote_per_adult || 0;
              const dmcPerCwb = opt.quote_per_cwb || 0;
              const dmcPerCnb = opt.quote_per_cnb || 0;
              const dmcTotal = opt.total_amount || 0;
              const badgeColor = idx === 0 ? 'bg-blue-100 text-blue-700' : idx === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700';
              return (
                <Card key={`admin-opt-${idx}`} className="bg-white border-gray-200 shadow-sm" data-testid={`admin-sidebar-option-card-${idx}`}>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <CreditCard className="w-5 h-5 text-emerald-600" />
                      DMC Price
                      <Badge className={`${badgeColor} text-[10px] font-bold tracking-wider ml-auto`}>OPTION {idx + 1}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {dmcPerAdult > 0 && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500">Per Adult</span>
                        <span className="font-medium text-gray-800">{formatCurrency(dmcPerAdult)}</span>
                      </div>
                    )}
                    {dmcPerCwb > 0 && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500">Per CWB</span>
                        <span className="font-medium text-blue-600">{formatCurrency(dmcPerCwb)}</span>
                      </div>
                    )}
                    {dmcPerCnb > 0 && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500">Per CNB</span>
                        <span className="font-medium text-amber-600">{formatCurrency(dmcPerCnb)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                      <span className="text-gray-700 font-medium">Total Amount</span>
                      <span className="text-xl font-bold text-emerald-600">{formatCurrency(dmcTotal)}</span>
                    </div>
                    {quote.gst_required && (
                      <>
                        <div className="flex justify-between items-center text-sm pt-2">
                          <span className="text-gray-500">GST @ 5%</span>
                          <span className="font-medium text-gray-700">{formatCurrency(Math.round(dmcTotal * 0.05))}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-emerald-200 bg-emerald-50 -mx-6 px-6 pb-1 -mb-4 rounded-b-lg" data-testid="total-with-gst">
                          <span className="text-emerald-800 font-semibold">Total with GST</span>
                          <span className="text-xl font-bold text-emerald-700">{formatCurrency(Math.round(dmcTotal * 1.05))}</span>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            {/* Single-option DMC Price (legacy / fallback) */}
            {!hasMultipleOptions && (
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CreditCard className="w-5 h-5 text-emerald-600" />
                  DMC Price
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {quote.quote_per_adult > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">Per Adult</span>
                    <span className="font-medium text-gray-800">{formatCurrency(quote.quote_per_adult)}</span>
                  </div>
                )}
                {quote.quote_per_cwb > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">Per CWB</span>
                    <span className="font-medium text-blue-600">{formatCurrency(quote.quote_per_cwb)}</span>
                  </div>
                )}
                {quote.quote_per_cnb > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">Per CNB</span>
                    <span className="font-medium text-amber-600">{formatCurrency(quote.quote_per_cnb)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                  <span className="text-gray-700 font-medium">Total Amount</span>
                  <span className="text-xl font-bold text-emerald-600">{formatCurrency(quote.quote_amount)}</span>
                </div>
                {quote.gst_required && (
                  <>
                    <div className="flex justify-between items-center text-sm pt-2">
                      <span className="text-gray-500">GST @ 5%</span>
                      <span className="font-medium text-gray-700">{formatCurrency(Math.round(quote.quote_amount * 0.05))}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-emerald-200 bg-emerald-50 -mx-6 px-6 pb-1 -mb-4 rounded-b-lg" data-testid="total-with-gst">
                      <span className="text-emerald-800 font-semibold">Total with GST</span>
                      <span className="text-xl font-bold text-emerald-700">{formatCurrency(Math.round(quote.quote_amount * 1.05))}</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
            )}

            {/* Agent Info */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <User className="w-5 h-5 text-blue-600" /> Agent Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Agent Name</p>
                    <p className="font-semibold text-gray-800">{quote.agent_name || '-'}</p>
                  </div>
                </div>
                {quote.agent_company && (
                  <div className="flex items-start gap-3">
                    <Briefcase className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Company</p>
                      <p className="text-gray-800">{quote.agent_company}</p>
                    </div>
                  </div>
                )}
                {quote.agent_designation && (
                  <div className="flex items-start gap-3">
                    <Briefcase className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Designation</p>
                      <p className="text-gray-800">{quote.agent_designation}</p>
                    </div>
                  </div>
                )}
                {quote.agent_phone && (
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Mobile</p>
                      <p className="text-emerald-600 font-medium">{quote.agent_phone}</p>
                    </div>
                  </div>
                )}
                {quote.agent_email && (
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="text-blue-600 text-sm break-all">{quote.agent_email}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* DMC User Details */}
            {(quote.assigned_to_name || quote.created_by_name) && (
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <User className="w-5 h-5 text-blue-600" /> DMC User Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">User Name</p>
                      <p className="font-semibold text-gray-800">{quote.assigned_to_name || quote.created_by_name || 'Admin'}</p>
                    </div>
                  </div>
                  {quote.assigned_user_designation && (
                    <div className="flex items-start gap-3">
                      <Briefcase className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Designation</p>
                        <p className="text-gray-800">{quote.assigned_user_designation}</p>
                      </div>
                    </div>
                  )}
                  {quote.assigned_user_phone && (
                    <div className="flex items-start gap-3">
                      <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Mobile</p>
                        <p className="text-emerald-600 font-medium">{quote.assigned_user_phone}</p>
                      </div>
                    </div>
                  )}
                  {quote.assigned_user_email && (
                    <div className="flex items-start gap-3">
                      <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="text-blue-600 text-sm break-all">{quote.assigned_user_email}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Timeline */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Clock className="w-5 h-5 text-gray-600" /> Timeline
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-2.5 h-2.5 rounded-full bg-sky-500 mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Quotation Requested</p>
                    <p className="text-sm text-gray-500">
                      {quote.created_at ? (() => { const d = new Date(quote.created_at); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; })() : '-'}
                    </p>
                  </div>
                </div>
                {quote.updated_at && quote.status !== 'pending' && (
                  <div className="flex items-start gap-3">
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Quotation Sent</p>
                      <p className="text-sm text-gray-500">
                        {(() => { const d = new Date(quote.updated_at); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; })()}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Action Buttons - below Timeline */}
            <Card className="bg-white border-gray-200 shadow-sm mt-4" data-testid="qp-actions-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base text-gray-700">
                  <Send className="w-4 h-4 text-emerald-600" /> Share Quotation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  onClick={() => downloadPdf(true)}
                  disabled={downloadingPdf}
                  className="w-full justify-start gap-2 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 font-semibold"
                  variant="outline"
                  data-testid="qp-download-pdf-with-price-btn"
                >
                  {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Download PDF (With Price)
                </Button>
                <Button
                  onClick={() => downloadPdf(false)}
                  disabled={downloadingPdf}
                  className="w-full justify-start gap-2 bg-rose-50/70 hover:bg-rose-100 text-rose-700 border border-rose-200 font-semibold"
                  variant="outline"
                  data-testid="qp-download-pdf-without-price-btn"
                >
                  {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Download PDF (Without Price)
                </Button>
                <Button
                  onClick={downloadDocx}
                  disabled={downloadingDocx}
                  className="w-full justify-start gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 font-semibold"
                  variant="outline"
                  data-testid="qp-download-docx-btn"
                >
                  {downloadingDocx ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileType className="w-4 h-4" />}
                  Download Word
                </Button>
                <Button
                  onClick={openEmailDialog}
                  className="w-full justify-start gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                  data-testid="qp-share-email-btn"
                >
                  <Send className="w-4 h-4" />
                  Share on Email
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Revision History */}
        {quote.revision_history && quote.revision_history.length > 0 && (
          <Card className="bg-white border-gray-200 shadow-sm mt-6">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <RotateCcw className="w-5 h-5 text-orange-500" />
                Revision History
                <Badge className="bg-orange-100 text-orange-700 text-xs ml-2">
                  {quote.revision_history.length} Revision{quote.revision_history.length > 1 ? 's' : ''}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {[...quote.revision_history].reverse().map((rev, idx) => (
                <div key={idx} className={`rounded-lg p-3 border ${rev.status === 'pending' ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-700">Revision #{rev.revision_num}</span>
                      {rev.status === 'pending' ? (
                        <Badge className="bg-orange-100 text-orange-600 text-[10px]">Pending</Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-600 text-[10px]">Completed</Badge>
                      )}
                    </div>
                    <span className="text-[10px] text-gray-400">{formatDate(rev.request_date)}</span>
                  </div>
                  {rev.categories && rev.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-1.5">
                      {rev.categories.map((cat, ci) => (
                        <span key={ci} className="text-[10px] bg-white border border-gray-200 rounded px-1.5 py-0.5 text-gray-600">{cat}</span>
                      ))}
                    </div>
                  )}
                  <p className="text-sm text-gray-700">{rev.notes}</p>
                  {rev.completed_date && (
                    <p className="text-[10px] text-emerald-600 mt-1">Completed: {formatDate(rev.completed_date)}</p>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Share on Email Dialog */}
        <Dialog open={emailDialog} onOpenChange={setEmailDialog}>
          <DialogContent className="max-w-lg bg-white border-gray-200" data-testid="qp-email-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-emerald-600" /> Share Quotation by Email</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-sm">To Email *</Label>
                <Input value={emailForm.to_email} onChange={e => setEmailForm({ ...emailForm, to_email: e.target.value })} placeholder="client@example.com" type="email" className="mt-1 bg-gray-50 border-gray-200" data-testid="qp-email-to" />
              </div>
              <div>
                <Label className="text-sm">Subject</Label>
                <Input value={emailForm.subject} onChange={e => setEmailForm({ ...emailForm, subject: e.target.value })} className="mt-1 bg-gray-50 border-gray-200" data-testid="qp-email-subject" />
              </div>
              <div>
                <Label className="text-sm">Message</Label>
                <Textarea value={emailForm.message} onChange={e => setEmailForm({ ...emailForm, message: e.target.value })} rows={5} className="mt-1 bg-gray-50 border-gray-200" data-testid="qp-email-message" />
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-700 pt-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={emailForm.include_pdf} onChange={e => setEmailForm({ ...emailForm, include_pdf: e.target.checked })} className="w-4 h-4 accent-emerald-600" data-testid="qp-email-include-pdf" />
                  Attach PDF
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={emailForm.include_docx} onChange={e => setEmailForm({ ...emailForm, include_docx: e.target.checked })} className="w-4 h-4 accent-emerald-600" data-testid="qp-email-include-docx" />
                  Attach Word
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={emailForm.cc_self} onChange={e => setEmailForm({ ...emailForm, cc_self: e.target.checked })} className="w-4 h-4 accent-emerald-600" data-testid="qp-email-cc-self" />
                  CC me
                </label>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setEmailDialog(false)} className="flex-1 border-gray-300">Cancel</Button>
                <Button onClick={sendEmail} disabled={sendingEmail} className="flex-1 bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="qp-email-send-btn">
                  {sendingEmail ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <><Send className="w-4 h-4" /> Send Email</>}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Direct Client — Guest Information Dialog */}
        <Dialog open={guestDialogOpen} onOpenChange={setGuestDialogOpen}>
          <DialogContent className="max-w-md bg-white border-gray-200" data-testid="qp-guest-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><User className="w-5 h-5 text-emerald-600" /> Guest Information</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-500 -mt-2">Fill or edit guest details to confirm this Direct Client booking.</p>
            <div className="space-y-3 pt-2">
              <div>
                <Label className="text-sm">Guest Name *</Label>
                <Input
                  value={guestForm.guest_name}
                  onChange={e => setGuestForm({ ...guestForm, guest_name: e.target.value })}
                  placeholder="Full name as per ID"
                  className="mt-1 bg-gray-50 border-gray-200"
                  data-testid="qp-guest-name"
                />
              </div>
              <div>
                <Label className="text-sm">Guest Mobile *</Label>
                <Input
                  value={guestForm.guest_mobile}
                  onChange={e => setGuestForm({ ...guestForm, guest_mobile: e.target.value })}
                  placeholder="+91 ..."
                  className="mt-1 bg-gray-50 border-gray-200"
                  data-testid="qp-guest-mobile"
                />
              </div>
              <div>
                <Label className="text-sm">Guest Email</Label>
                <Input
                  type="email"
                  value={guestForm.guest_email}
                  onChange={e => setGuestForm({ ...guestForm, guest_email: e.target.value })}
                  placeholder="guest@example.com"
                  className="mt-1 bg-gray-50 border-gray-200"
                  data-testid="qp-guest-email"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setGuestDialogOpen(false)}
                  className="flex-1 border-gray-300"
                  data-testid="qp-guest-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmDirectBooking}
                  disabled={confirmingBooking}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 gap-2"
                  data-testid="qp-guest-confirm-btn"
                >
                  {confirmingBooking ? <><Loader2 className="w-4 h-4 animate-spin" /> Confirming...</> : <><CheckCircle className="w-4 h-4" /> Confirm Booking</>}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminQuotePreview;
